import { SplideSlide } from '@splidejs/react-splide'
import React, { useRef, useEffect, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import * as echarts from 'echarts' // Import echarts to access `graphic`
import HomeFutureMineralsPricesChange from './HomeFutureMineralsPricesChange'
import moment from 'moment'
import { tailwindColors } from '../../../config/global'
import axiosClient from '../../../config/axios-client'
import { Link } from 'react-router-dom'

const normalizeChartData = (prices = [], dates = []) => {
  if (!Array.isArray(prices) || !Array.isArray(dates)) {
    return { prices: [], dates: [] }
  }

  return prices.reduce(
    (data, rawPrice, index) => {
      const price = Number(String(rawPrice).replace(/,/g, ''))
      const date = dates[index]

      if (Number.isFinite(price) && date) {
        data.prices.push(price)
        data.dates.push(date)
      }

      return data
    },
    { prices: [], dates: [] },
  )
}

export default function HomeFutureLastMonthPricesChart({ mineral, lng }) {
  const chartRef = useRef(null)

  // The home prices endpoint only returns points from the last 30 days, which
  // can come back empty when the latest data is older. Fall back to the
  // per-mineral monthly series (same source the prices page uses) so the
  // sparkline always renders.
  const [series, setSeries] = useState({
    prices: mineral.prices ?? [],
    dates: mineral.dates ?? [],
  })

  useEffect(() => {
    const hasPrices = Array.isArray(mineral.prices) && mineral.prices.length > 0

    if (hasPrices) {
      setSeries({ prices: mineral.prices, dates: mineral.dates ?? [] })
      return
    }

    let isMounted = true
    axiosClient
      .get(`/platform/minerals/${mineral.slug}/prices?INTERVAL=MONTH`)
      .then(({ data }) => {
        if (!isMounted) return
        setSeries({
          prices: data?.mineral?.prices ?? [],
          dates: data?.mineral?.dates ?? [],
        })
      })
      .catch(() => {})

    return () => {
      isMounted = false
    }
  }, [mineral.slug, mineral.prices, mineral.dates])

  // Force chart resize after mounting and whenever the series changes
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.getEchartsInstance().resize()
    }
  }, [series])

  // Prepare the chart option for the single chart
  const getChartOption = (prices, dates, change, unit) => {
    const chartData = normalizeChartData(prices, dates)
    const minPrice = Math.min(...chartData.prices)
    const maxPrice = Math.max(...chartData.prices)
    const priceRange = maxPrice - minPrice
    const safePriceRange = priceRange === 0 ? Math.abs(minPrice) || 1 : priceRange
    const orderOfMagnitude = Math.pow(10, Math.floor(Math.log10(safePriceRange)))
    const stepSize = orderOfMagnitude / 10
    const adjustedMin = minPrice - stepSize
    const adjustedMax = maxPrice + stepSize

    return {
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: chartData.dates,
        show: false,
      },
      yAxis: {
        type: 'value',
        min: adjustedMin,
        max: adjustedMax,
        interval: stepSize,
        splitNumber: 5,
        show: false,
      },
      series: [
        {
          data: chartData.prices,
          type: 'line',
          smooth: true, // Enable smooth line
          symbol: 'none', // Ensure no markers on data points
          lineStyle: {
            color: tailwindColors.secondary, // Match the prices page AREA chart
            width: 2,
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: `${tailwindColors.secondary}cc` }, // Top color
              { offset: 1, color: `${tailwindColors.secondary}1a` }, // Bottom color
            ]),
          },
        },
      ],
      tooltip: {
        trigger: 'axis',
        formatter: ({ 0: { value, name } }) => {
          return `
            <div class="flex flex-col gap-1">
              <div class="flex flex-row-reverse gap-2 items-end">
                <div class="font-bold">Date</div>
                <div>${moment(name).format('MMMM Do YYYY')}</div>
              </div>
              <div class="flex flex-row-reverse gap-2 items-end">
                <div class="font-bold">Price</div>
                  <div dir="ltr">$ ${Number.isInteger(value) ? value.toFixed(2) : value} / ${unit}</div>
              </div>
            </div>
          `
        },
      },
      grid: {
        left: '0%',
        right: '0%',
        bottom: '0%',
        top: '10%',
        containLabel: false,
      },
    }
  }

  const chartData = normalizeChartData(series.prices, series.dates)

  return (
    <SplideSlide
      key={mineral.id}
      className="relative cursor-pointer overflow-hidden border-2 border-muted-300 bg-white text-center hover:border-secondary-600 hover:bg-muted-300"
    >
      <Link
        to={`/${lng}/minerals/${mineral.slug}/prices?CHART=AREA&INTERVAL=MONTH`}
        className="flex flex-col justify-between p-2"
      >
        <div className="flex items-start justify-between ltr:flex-row-reverse">
          <div className="flex gap-2 ltr:flex-row-reverse ltr:text-end rtl:text-start">
            <div className="border-2 border-muted-300 bg-light p-1">
              <img
                className="w-10"
                src={`${process.env.REACT_APP_API_BASE_URL}/storage/minerals/${mineral.image}`}
                alt={mineral[`name_${lng}`]}
              />
            </div>
            <div className="font-bold">
              <div>{mineral[`name_${lng}`].replace('(', '').replace(')', '')}</div>
              <div className="text-secondary-600">{mineral.price} $</div>
            </div>
          </div>
          <HomeFutureMineralsPricesChange change={mineral.change} />
        </div>
        <div className="mt-4 flex !h-full">
          {chartData.prices.length > 0 && (
            <ReactECharts
              ref={chartRef}
              option={getChartOption(series.prices, series.dates, mineral.change, mineral[`unit_symbol`])}
              className="!h-32 w-full"
              style={{ height: 128, width: '100%' }}
            />
          )}
        </div>
      </Link>
    </SplideSlide>
  )
}
